import 'reactflow/dist/style.css';
import './components/rules/ReactFlowConceptMap.css';
import React, { Suspense, useEffect } from "react";
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { SocketProvider } from "./context/SocketContext";
import { DataProvider } from "./context/DataContext";
import { ContactsProvider } from "./context/ContactsContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import useResizeObserverFix from "./hooks/useResizeObserverFix";

import { RecoveryProvider } from "./context/RecoveryContext";

// Elementos de UI críticos
import ProtectedRoute from "./components/layout/ProtectedRoute";
import AppLayout from "./components/layout/AppLayout";
import PageLoader from "./components/layout/PageLoader";
import ErrorBoundary from "./components/ui/ErrorBoundary";
import { PlanBadge, LimitAlert, FeatureGate } from './components/permissions';
import MessageRecoveryPanel from "./components/chat/MessageRecoveryPanel";

// --- Lazy loading de páginas ---
const RegisterPage = React.lazy(() => import("./pages/RegisterPage"));
const LoginPage = React.lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = React.lazy(() => import("./pages/ForgotPasswordPage"));
const DashboardPage = React.lazy(() => import("./pages/DashboardPage"));
const ChatPage = React.lazy(() => import("./pages/ChatPage"));
const RulesPage = React.lazy(() => import("./pages/RulesPage"));
const ContactsPage = React.lazy(() => import("./pages/ContactsPage"));
const MassMessagingPage = React.lazy(() => import("./pages/MassMessagingPage"));
const MediaLibraryPage = React.lazy(() => import("./pages/MediaLibraryPage"));
const AIAnalyticsDashboard = React.lazy(() => import("./components/ai/AIAnalyticsDashboard"));
const IntelligentFlowsPage = React.lazy(() => import("./pages/IntelligentFlowsPage"));
const AIAssistantPage = React.lazy(() => import("./pages/AIAssistantPage"));
const AdminPage = React.lazy(() => import("./pages/AdminPage"));
const AdminUsers = React.lazy(() => import("./components/admin/AdminUsers"));
const PricingPlans = React.lazy(() => import("./components/billing/ModernPricingPlans"));
const MyAccount = React.lazy(() => import("./components/billing/MyAccount"));
const SubscriptionSuccessPage = React.lazy(() => import("./pages/SubscriptionSuccessPage"));
const SubscriptionCancelPage = React.lazy(() => import("./pages/SubscriptionCancelPage"));
const SystemDiagnostics = React.lazy(() => import("./components/admin/SystemDiagnostics"));
const QRLinkPage = React.lazy(() => import("./pages/QRLinkPage"));
const FlowAnalyticsPage = React.lazy(() => import("./pages/FlowAnalyticsPage"));
const SynonymsPage = React.lazy(() => import("./pages/SynonymsPage"));
const ActionsPage = React.lazy(() => import("./pages/ActionsPage"));
const TelegramSettings = React.lazy(() => import("./pages/TelegramSettings"));
const EmailSetupPage = React.lazy(() => import("./pages/EmailSetupPage"));
const WhatsAppTemplatesPage = React.lazy(() => import("./pages/WhatsAppTemplatesPage"));
const TermsOfService = React.lazy(() => import("./pages/TermsOfService"));
const PrivacyPolicy = React.lazy(() => import("./pages/PrivacyPolicy"));
const PackageSuccessPage = React.lazy(() => import("./pages/PackageSuccessPage"));
const BoostSuccessPage = React.lazy(() => import("./pages/BoostSuccessPage"));
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const WhatsAppSetupPage = React.lazy(() => import("./pages/WhatsAppSetupPage"));
const WhatsAppSetupPageSimple = React.lazy(() => import("./pages/WhatsAppSetupPageSimple"));
const WhatsAppSetupPageV2 = React.lazy(() => import("./pages/WhatsAppSetupPageV2"));
const DiagnosticPage = React.lazy(() => import("./pages/DiagnosticPage"));
// 🔍 Mirador del rediseño de Contactos — solo desarrollo, sin sesión, sin API
const ContactsUIPreview = React.lazy(() => import("./pages/dev/ContactsUIPreview"));
const DeveloperApiPage = React.lazy(() => import("./pages/DeveloperApiPage"));
const DesktopClientPage = React.lazy(() => import("./pages/DesktopClientPage"));
const ConnectionHubPage = React.lazy(() => import("./pages/ConnectionHubPage"));
const WhatsAppWebSettings = React.lazy(() => import("./pages/WhatsAppWebSettings"));
const WhatsAppAPISetupPage = React.lazy(() => import("./pages/WhatsAppAPISetupPage"));
const WhatsAppAPIHelpPage = React.lazy(() => import("./pages/WhatsAppAPIHelpPage"));
const FlowEditorPage = React.lazy(() => import("./pages/FlowEditorPage"));
const MobileDevicesPage = React.lazy(() => import("./pages/MobileDevicesPage"));
const TaxiDispatchPage = React.lazy(() => import("./pages/TaxiDispatchPage")); // 🚕 Taxi Dispatch

const AlertsPage = React.lazy(() => import("./pages/AlertsPage"));
const MobileAccessPage = React.lazy(() => import("./pages/MobileAccessPage"));

// Página 404 personalizada
const NotFoundPage = React.lazy(() => import("./pages/NotFoundPage"));

// Redirección inteligente en raíz (cargador compartido con el layout).
const RootRoute = () => {
  const { currentUser, loading } = useAuth();

  if (loading) return <PageLoader fullPage />;

  // Si está autenticado, redirigir al dashboard evitando cargar la Landing
  if (currentUser) {
    return <Navigate to="/dashboard" replace />;
  }

  // Si no está autenticado, renderizar Landing (se cargará lazy aquí)
  return <LandingPage />;
};

// Layout que envuelve toda la app con los providers necesarios
const RootLayout = () => {
  // Fix global para suprimir el error de ResizeObserver
  useResizeObserverFix();

  // Cleanup robusto del Splash Screen
  useEffect(() => {
    const splash = document.getElementById('splash-screen');
    if (!splash) return;

    // Pequeño delay para asegurar carga inicial
    const fadeTimer = setTimeout(() => {
      splash.style.opacity = '0';
      splash.style.transition = 'opacity 0.5s ease-out';

      const removeTimer = setTimeout(() => {
        splash.remove();
      }, 500);

      return () => clearTimeout(removeTimer);
    }, 500);

    return () => clearTimeout(fadeTimer);
  }, []);

  return (
    <ErrorBoundary>
      <ThemeProvider>
        <AuthProvider>
          <SocketProvider>
            <DataProvider>
              <ContactsProvider>
                <RecoveryProvider>
                  <Suspense fallback={<PageLoader fullPage />}>
                    <Outlet />
                  </Suspense>
                  <MessageRecoveryPanel />
                </RecoveryProvider>
              </ContactsProvider>
            </DataProvider>
          </SocketProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
};

// Definición de rutas con createBrowserRouter
const router = createBrowserRouter([
  {
    // Layout raíz que provee todos los contextos
    element: <RootLayout />,
    children: [
      // --- Rutas Públicas ---
      { path: "/", element: <RootRoute /> },
      { path: "/register", element: <RegisterPage /> },
      { path: "/login", element: <LoginPage /> },
      { path: "/forgot-password", element: <ForgotPasswordPage /> },
      { path: "/qr/:token", element: <QRLinkPage /> },
      { path: "/terms", element: <TermsOfService /> },
      { path: "/privacy", element: <PrivacyPolicy /> },
      { path: "/mobile-access", element: <MobileAccessPage /> },

      // Rutas de diagnóstico/setup (solo en desarrollo)
      ...(process.env.NODE_ENV === 'development' ? [
        { path: "/diagnostic", element: <DiagnosticPage /> },
        { path: "/ui-contactos", element: <ContactsUIPreview /> },
        { path: "/whatsapp-setup-old", element: <WhatsAppSetupPage /> },
        { path: "/whatsapp-setup-simple", element: <WhatsAppSetupPageSimple /> },
      ] : []),

      // --- Rutas Protegidas ---
      {
        element: <ProtectedRoute />,
        children: [
          {
            element: <AppLayout />,
            children: [
              { path: "/dashboard", element: <DashboardPage /> },
              { 
                path: "/chat", 
                element: (
                  <FeatureGate feature="liveChat" plan="pro" fullPage={true}>
                    <ChatPage />
                  </FeatureGate>
                ) 
              },
              { 
                path: "/ai-analytics", 
                element: (
                  <FeatureGate feature="ai_analytics" plan="pro" fullPage={true}>
                    <AIAnalyticsDashboard />
                  </FeatureGate>
                ) 
              },
              { 
                path: "/intelligent-flows", 
                element: (
                  <FeatureGate feature="intelligent_flows" plan="pro" fullPage={true}>
                    <IntelligentFlowsPage />
                  </FeatureGate>
                ) 
              },
              { 
                path: "/ai-assistant", 
                element: (
                  <FeatureGate feature="ai_assistant" plan="pro" fullPage={true}>
                    <AIAssistantPage />
                  </FeatureGate>
                ) 
              },
              { path: "/rules", element: <RulesPage /> },
              { path: "/flow-editor/:ruleId", element: <FlowEditorPage /> },
              { 
                path: "/contacts", 
                element: (
                  <FeatureGate feature="contacts" plan="pro" fullPage={true}>
                    <ContactsPage />
                  </FeatureGate>
                ) 
              },
              { 
                path: "/mass-messaging", 
                element: (
                  <FeatureGate feature="mass_messaging" plan="pro" fullPage={true}>
                    <MassMessagingPage />
                  </FeatureGate>
                ) 
              },
              { path: "/media", element: <MediaLibraryPage /> },
              { 
                path: "/flow-analytics", 
                element: (
                  <FeatureGate feature="flow_analytics" plan="pro" fullPage={true}>
                    <FlowAnalyticsPage />
                  </FeatureGate>
                ) 
              },
              { 
                path: "/synonyms", 
                element: (
                  <FeatureGate feature="ai_intent" plan="pro" fullPage={true}>
                    <SynonymsPage />
                  </FeatureGate>
                ) 
              },
              { 
                path: "/actions", 
                element: (
                  <FeatureGate feature="advanced_rules" plan="pro" fullPage={true}>
                    <ActionsPage />
                  </FeatureGate>
                ) 
              },
              { path: "/telegram", element: <TelegramSettings /> },
              { path: "/email-setup", element: <EmailSetupPage /> },
              { path: "/whatsapp-plantillas", element: <WhatsAppTemplatesPage /> },
              { path: "/whatsapp-setup", element: <WhatsAppSetupPageV2 /> },
              { path: "/developers", element: <DeveloperApiPage /> },
              { path: "/desktop-client", element: <DesktopClientPage /> },
              { path: "/connections", element: <ConnectionHubPage /> },
              { path: "/whatsapp-web", element: <WhatsAppWebSettings /> },
              { path: "/whatsapp-api-setup", element: <WhatsAppAPISetupPage /> },
              { path: "/whatsapp-api-help", element: <WhatsAppAPIHelpPage /> },
              { path: "/mobile-devices", element: <MobileDevicesPage /> },
              { path: "/avisos", element: <AlertsPage /> },
              { 
                path: "/taxi-dispatch", 
                element: (
                  <FeatureGate feature="taxiDispatch" plan="business" fullPage={true}>
                    <TaxiDispatchPage />
                  </FeatureGate>
                ) 
              },


              { path: "/admin", element: <AdminPage /> },
              { path: "/admin/users", element: <AdminUsers /> },
              {
                path: "/admin/system",
                element: (
                  <ProtectedRoute requireAdmin={true}>
                    <SystemDiagnostics onClose={() => window.history.back()} />
                  </ProtectedRoute>
                )
              },

              { path: "/pricing", element: <PricingPlans /> },
              { path: "/my-account", element: <MyAccount /> },
              { path: "/subscription/success", element: <SubscriptionSuccessPage /> },
              { path: "/subscription/cancel", element: <SubscriptionCancelPage /> },
              { path: "/package-success", element: <PackageSuccessPage /> },
              { path: "/boost-success", element: <BoostSuccessPage /> },
              { path: "/subscription", element: <Navigate to="/pricing" replace /> },
            ]
          }
        ]
      },

      // Catch-all: Página 404 personalizada
      { path: "*", element: <NotFoundPage /> },
    ]
  }
]);

// App component ahora solo renderiza el RouterProvider
function App() {
  return <RouterProvider router={router} />;
}

export default App;
